import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check, ChevronDown, Send, Trash2, X } from 'lucide-react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { api, extractApiError } from '../lib/api'
import { workspaceMembersResponse } from '../lib/types'
import { z } from 'zod'
import { ACCESS_LEVEL_OPTIONS as ACCESS_OPTIONS } from '../lib/access-levels'

const AVATAR_PALETTE = [
  '#02367B',
  '#34D399',
  '#F87171',
  '#FBBF24',
  '#A78BFA',
  '#F472B6',
  '#0a2349',
  '#FB923C',
]
function avatarColor(name) {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]
}
function initials(name) {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Pretty-print a workspace member's effective access level. */
function memberAccessLabel(m) {
  return labelForAccessLevel(m.accessLevel)
}

function labelForAccessLevel(level) {
  if (level === 'view') return 'View only'
  if (level === 'comment') return 'Comment'
  if (level === 'full') return 'Full edit'
  return 'Edit'
}

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const pendingInvitation = z.object({
  id: z.string().uuid(),
  emailId: z.string(),
  accessLevel: z.string(),
  status: z.string(),
  expiresAt: z.string(),
  invitedByName: z.string(),
})
const pendingInvitationsResponse = z.object({ data: z.array(pendingInvitation) })

/** Google-Drive-style "Share / Invite Members" dialog. */
export function WorkspacePeopleDialog({
  open,
  workspaceId,
  onClose,
  onInvited,
  taskId,
}) {
  const qc = useQueryClient()
  // Shared cache key with SpaceShareDialog and the RealtimeInvalidator
  // map. Previously this dialog used `['workspaces', id, 'members']`,
  // which meant member edits made here didn't refresh SpaceShareDialog
  // and socket-driven `member` events didn't refresh this dialog.
  const membersQuery = useQuery({
    queryKey: ['workspace-members', workspaceId],
    queryFn: async () => {
      if (!workspaceId) throw new Error('Missing workspace id.')
      const res = await api.get(`/workspaces/${workspaceId}/members`)
      return workspaceMembersResponse.parse(res.data)
    },
    enabled: open && Boolean(workspaceId),
  })
  // Owner first, then everyone else alphabetically.
  const sortedMembers = useMemo(() => {
    const list = membersQuery.data?.data ?? []
    return [...list].sort((a, b) => {
      if (a.role === 'admin' && b.role !== 'admin') return -1
      if (b.role === 'admin' && a.role !== 'admin') return 1
      return a.fullName.localeCompare(b.fullName)
    })
  }, [membersQuery.data])

  const [emails, setEmails] = useState([])
  const [pending, setPending] = useState('')
  const [pendingError, setPendingError] = useState(null)
  const [accessLevel, setAccessLevel] = useState('edit')
  const [accessMenuOpen, setAccessMenuOpen] = useState(false)
  const [notify, setNotify] = useState(true)
  const [message, setMessage] = useState('')
  const inputRef = useRef(null)
  const accessTriggerRef = useRef(null)
  const accessMenuRef = useRef(null)

  // Reset every time the dialog reopens
  useEffect(() => {
    if (open) {
      setEmails([])
      setPending('')
      setPendingError(null)
      setAccessLevel('edit')
      setAccessMenuOpen(false)
      setNotify(true)
      setMessage('')
    }
  }, [open])

  // Close the access menu on outside click / Escape
  useEffect(() => {
    if (!accessMenuOpen) return
    function onClick(e) {
      const t = e.target
      if (
        !accessMenuRef.current?.contains(t) &&
        !accessTriggerRef.current?.contains(t)
      ) {
        setAccessMenuOpen(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setAccessMenuOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [accessMenuOpen])

  function commitPending() {
    const value = pending.trim().replace(/[,;]+$/, '')
    if (!value) return true
    if (!EMAIL_RX.test(value)) {
      setPendingError(`"${value}" is not a valid Email ID.`)
      return false
    }
    if (emails.some((e) => e.toLowerCase() === value.toLowerCase())) {
      setPending('')
      setPendingError(null)
      return true
    }
    setEmails((prev) => [...prev, value])
    setPending('')
    setPendingError(null)
    return true
  }

  function removeChip(email) {
    setEmails((prev) => prev.filter((e) => e !== email))
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab') {
      if (pending.trim()) {
        e.preventDefault()
        commitPending()
      }
    } else if (e.key === 'Backspace' && pending === '' && emails.length > 0) {
      // Backspace on empty input pops the last chip — Gmail-style.
      removeChip(emails[emails.length - 1])
    }
  }

  function handlePaste(e) {
    const text = e.clipboardData.getData('text')
    if (!text || !/[\s,;]/.test(text)) return
    e.preventDefault()
    const tokens = text.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean)
    const valid = []
    const invalid = []
    const existing = new Set(emails.map((x) => x.toLowerCase()))
    for (const t of tokens) {
      const lower = t.toLowerCase()
      if (existing.has(lower)) continue
      if (EMAIL_RX.test(t)) {
        valid.push(t)
        existing.add(lower)
      } else {
        invalid.push(t)
      }
    }
    if (valid.length > 0) setEmails((prev) => [...prev, ...valid])
    setPending('')
    setPendingError(invalid.length > 0 ? `Skipped invalid: ${invalid.join(', ')}` : null)
  }

  const invitationsQuery = useQuery({
    queryKey: ['workspaces', workspaceId, 'invitations'],
    queryFn: async () => {
      if (!workspaceId) throw new Error('Missing workspace id.')
      const res = await api.get(`/workspaces/${workspaceId}/invitations`)
      return pendingInvitationsResponse.parse(res.data)
    },
    enabled: open && Boolean(workspaceId),
  })
  const pendingInvites = invitationsQuery.data?.data ?? []

  const revokeMutation = useMutation({
    mutationFn: async (invitationId) => {
      await api.delete(`/invitations/${invitationId}`)
    },
    onSuccess: () => {
      toast.success('Invitation revoked.')
      qc.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'invitations'] })
    },
    onError: (err) => {
      const { message } = extractApiError(err)
      toast.error(message)
    },
  })

  const removeMutation = useMutation({
    mutationFn: async (userId) => {
      if (!workspaceId) throw new Error('Missing workspace id.')
      await api.delete(`/workspaces/${workspaceId}/members`, {
        data: { userIds: [userId] },
      })
    },
    onSuccess: () => {
      toast.success('Member removed successfully.')
      qc.invalidateQueries({ queryKey: ['workspace-members', workspaceId] })
      qc.invalidateQueries({ queryKey: ['members', workspaceId] })
    },
    onError: (err) => {
      const { message } = extractApiError(err)
      toast.error(message)
    },
  })
  const [confirmRemove, setConfirmRemove] = useState(null)

  const updateAccessMutation = useMutation({
    mutationFn: async (payload) => {
      if (!workspaceId) throw new Error('Missing workspace id.')
      await api.patch(
        `/workspaces/${workspaceId}/members/${payload.userId}`,
        { accessLevel: payload.accessLevel },
      )
    },
    onSuccess: () => {
      toast.success('Access updated successfully.')
      qc.invalidateQueries({ queryKey: ['workspace-members', workspaceId] })
      qc.invalidateQueries({ queryKey: ['members', workspaceId] })
    },
    onError: (err) => {
      const { message } = extractApiError(err)
      toast.error(message)
    },
  })

  const sendMutation = useMutation({
    mutationFn: async (payload) => {
      if (!workspaceId) throw new Error('Missing workspace id.')
      const res = await api.post(`/workspaces/${workspaceId}/members`, {
        inviteEmails: payload.emails,
        accessLevel: payload.accessLevel,
        taskId: taskId ?? undefined,
      })
      return res.data
    },
    onSuccess: (data) => {
      const added = data?.added ?? 0
      const invited = data?.invitedEmails ?? []
      if (added > 0) {
        toast.success(
          `${added} teammate${added === 1 ? '' : 's'} added.`,
        )
      }
      if (invited.length > 0) {
        toast.success(
          `${invited.length} invitation${invited.length === 1 ? '' : 's'} sent.`,
        )
      }
      if (added === 0 && invited.length === 0) {
        toast.info('Selected people already have access.')
      }
      if (onInvited && (data?.addedUserIds?.length ?? 0) > 0) {
        onInvited(data.addedUserIds ?? [])
      }
      qc.invalidateQueries({ queryKey: ['workspaces', 'mine'] })
      qc.invalidateQueries({ queryKey: ['workspace-members', workspaceId] })
      // SpacePage caches members under a different key — invalidate that too
      // so the assignee picker reflects the new addition without a page reload.
      qc.invalidateQueries({ queryKey: ['members', workspaceId] })
      onClose()
    },
    onError: (err) => {
      const { message } = extractApiError(err)
      toast.error(message)
    },
  })

  function send() {
    // Make sure whatever the user is mid-typing also gets included.
    if (pending.trim() && !commitPending()) return
    if (emails.length === 0) {
      setPendingError('Add at least one Email ID.')
      inputRef.current?.focus()
      return
    }
    sendMutation.mutate({ emails, accessLevel })
  }

  const currentAccess = ACCESS_OPTIONS.find((o) => o.value === accessLevel) ?? ACCESS_OPTIONS[1]

  // Two modes:
  //   idle    → show the People-with-access list and a single Done button
  //   inviting → show access dropdown + Notify + Message and Cancel / Send
  const inviting = emails.length > 0 || pending.trim().length > 0

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invite Members"
      size="md"
      footer={
        inviting ? (
          <>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={sendMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              loading={sendMutation.isPending}
              disabled={emails.length === 0 && !pending.trim()}
              onClick={send}
            >
              <Send size={14} />
              Send
            </Button>
          </>
        ) : (
          <Button type="button" className="ml-auto" onClick={onClose}>
            Done
          </Button>
        )
      }
    >
      <div className="space-y-4">
        {/* Chip input + role dropdown */}
        <div>
          <label
            htmlFor="invite-input"
            className="block w-fit mb-1.5 text-sm font-semibold text-[color:var(--color-ink-strong)]"
          >
            Add people by Email ID
          </label>
          <div className="flex items-stretch gap-2">
            <div
              className={
                'flex-1 min-w-0 flex flex-wrap items-center gap-1.5 px-2 py-1.5 rounded-md border bg-[color:var(--color-card)] focus-within:border-[color:var(--color-accent)] ' +
                (pendingError
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]')
              }
              onClick={() => inputRef.current?.focus()}
            >
              {emails.map((e) => (
                <span
                  key={e}
                  className="inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 rounded-full bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] text-sm text-[color:var(--color-ink-strong)]"
                >
                  {e}
                  <button
                    type="button"
                    onClick={() => removeChip(e)}
                    aria-label={`Remove ${e}`}
                    className="p-0.5 rounded-full text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              <input
                ref={inputRef}
                id="invite-input"
                type="text"
                value={pending}
                onChange={(e) => {
                  setPending(e.target.value)
                  if (pendingError) setPendingError(null)
                }}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={() => commitPending()}
                placeholder={emails.length === 0 ? 'Enter Email ID' : ''}
                className="flex-1 min-w-[8rem] outline-none bg-transparent text-sm text-[color:var(--color-ink)] placeholder:text-[color:var(--color-ink-subtle)] py-1"
              />
            </div>

            {/* Access-level picker — only shown once the user starts inviting. */}
            {inviting ? (
              <div className="relative shrink-0">
                <button
                  ref={accessTriggerRef}
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={accessMenuOpen}
                  onClick={() => setAccessMenuOpen((v) => !v)}
                  className="h-full inline-flex items-center gap-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] pl-3 pr-2 cursor-pointer hover:bg-[color:var(--color-card-hover)]"
                >
                  <span className="font-medium">{currentAccess.label}</span>
                  <ChevronDown
                    size={14}
                    className={
                      'text-[color:var(--color-ink-muted)] transition-transform ' +
                      (accessMenuOpen ? 'rotate-180' : '')
                    }
                  />
                </button>

                {accessMenuOpen ? (
                  <div
                    ref={accessMenuRef}
                    role="listbox"
                    aria-label="Access level"
                    className="absolute right-0 top-full mt-1.5 z-30 w-72 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl overflow-hidden"
                  >
                    {ACCESS_OPTIONS.map((opt) => {
                      const selected = opt.value === accessLevel
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => {
                            setAccessLevel(opt.value)
                            setAccessMenuOpen(false)
                          }}
                          className={
                            'w-full text-left px-4 py-3 cursor-pointer hover:bg-[color:var(--color-card-hover)] ' +
                            (selected ? 'bg-[color:var(--color-card-hover)]' : '')
                          }
                        >
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                                {opt.label}
                              </p>
                              <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
                                {opt.description}
                              </p>
                            </div>
                            {selected ? (
                              <Check
                                size={14}
                                className="mt-0.5 text-[color:var(--color-accent-dim)] shrink-0"
                              />
                            ) : null}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          {pendingError ? (
            <p
              role="alert"
              className="mt-1 text-xs text-[color:var(--color-priority-urgent)]"
            >
              {pendingError}
            </p>
          ) : (
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              Press Enter or comma to add. Only people already in your
              company can be invited.
            </p>
          )}
        </div>

        {/* People with access — only shown when the user is NOT actively inviting. */}
        {!inviting ? (
        <div className="pt-2 border-t border-[color:var(--color-rule)]">
          <p className="pt-3 mb-2 text-sm font-semibold text-[color:var(--color-ink-strong)]">
            People with access
          </p>
          {membersQuery.isLoading ? (
            <p className="text-xs text-[color:var(--color-ink-muted)]">Loading members…</p>
          ) : membersQuery.isError ? (
            <p className="text-xs text-[color:var(--color-priority-urgent)]">
              {extractApiError(membersQuery.error).message}
            </p>
          ) : sortedMembers.length === 0 ? (
            <p className="text-xs text-[color:var(--color-ink-muted)]">
              You're the only member with access.
            </p>
          ) : (
            <ul className="rounded-md border border-[color:var(--color-rule)] divide-y divide-[color:var(--color-rule)] max-h-56 overflow-y-auto">
              {sortedMembers.map((m) => {
                const isOwner = m.role === 'admin'
                return (
                  <li
                    key={m.userId}
                    className="flex items-center gap-3 px-3 py-2.5"
                  >
                    <span
                      className="h-8 w-8 rounded-full overflow-hidden flex items-center justify-center text-xs font-semibold text-white shrink-0"
                      style={{
                        backgroundColor: m.avatarUrl ? 'transparent' : avatarColor(m.fullName),
                      }}
                    >
                      {m.avatarUrl ? (
                        <img
                          src={m.avatarUrl}
                          alt={m.fullName}
                          className="h-full w-full object-cover"
                          draggable={false}
                        />
                      ) : (
                        initials(m.fullName)
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate flex items-center gap-2">
                        {m.fullName}
                        {isOwner ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold uppercase tracking-wider bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-dim)] border border-[color:var(--color-accent-border)]">
                            Owner
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-[color:var(--color-ink-muted)] truncate">
                        {m.emailId}
                      </p>
                    </div>
                    {isOwner ? (
                      <span className="text-xs font-medium text-[color:var(--color-ink-subtle)]">
                        {memberAccessLabel(m)}
                      </span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <RowAccessPicker
                          currentLevel={m.accessLevel}
                          disabled={updateAccessMutation.isPending}
                          onChange={(level) =>
                            updateAccessMutation.mutate({ userId: m.userId, accessLevel: level })
                          }
                        />
                        <button
                          type="button"
                          onClick={() => setConfirmRemove(m)}
                          disabled={removeMutation.isPending}
                          title="Remove access"
                          aria-label="Remove access"
                          className="p-1.5 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        ) : null}

        {/* Notify people + Message — only shown when actively inviting. */}
        {inviting ? (
          <>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
              />
              <span className="text-sm text-[color:var(--color-ink)]">
                Notify people
              </span>
            </label>

            {notify ? (
              <div>
                <label
                  htmlFor="invite-message"
                  className="block w-fit mb-1.5 text-sm font-semibold text-[color:var(--color-ink-strong)]"
                >
                  Message
                  <span className="ml-2 text-xs font-normal text-[color:var(--color-ink-muted)]">
                    Optional
                  </span>
                </label>
                <textarea
                  id="invite-message"
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Enter a Message"
                  className={
                    'w-full px-3 py-2 rounded-md border bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] placeholder:text-[color:var(--color-ink-subtle)] outline-none focus:border-[color:var(--color-accent)] resize-none ' +
                    (message.length > 500
                      ? 'border-[color:var(--color-priority-urgent)]'
                      : 'border-[color:var(--color-rule)]')
                  }
                />
                <p
                  className={
                    'mt-1 text-xs ' +
                    (message.length > 500
                      ? 'text-[color:var(--color-priority-urgent)] font-semibold'
                      : 'text-[color:var(--color-ink-muted)]')
                  }
                >
                  {message.length > 500 ? 'Message must be at most 500 characters. ' : ''}
                  {message.length}/500
                </p>
              </div>
            ) : null}
          </>
        ) : null}

        {/* Pending invitations — admins can revoke before the invitee accepts. */}
        {!inviting && pendingInvites.length > 0 ? (
          <div className="mt-4">
            <p className="mb-1.5 text-sm font-semibold text-[color:var(--color-ink-strong)]">
              Pending invitations
              <span className="ml-2 text-xs font-normal text-[color:var(--color-ink-muted)]">
                {pendingInvites.length}
              </span>
            </p>
            <ul className="rounded-md border border-[color:var(--color-rule)] divide-y divide-[color:var(--color-rule)] max-h-48 overflow-y-auto">
              {pendingInvites.map((inv) => (
                <li
                  key={inv.id}
                  className="flex items-center gap-3 px-3 py-2.5"
                >
                  <span
                    className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0 bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-muted)]"
                    style={{ backgroundColor: avatarColor(inv.emailId) }}
                  >
                    {inv.emailId.slice(0, 2).toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[color:var(--color-ink)] truncate">
                      {inv.emailId}
                    </p>
                    <p className="text-xs text-[color:var(--color-ink-muted)] truncate">
                      Invited by {inv.invitedByName} · pending
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => revokeMutation.mutate(inv.id)}
                    disabled={revokeMutation.isPending}
                    title="Revoke invitation"
                    aria-label="Revoke invitation"
                    className="p-1.5 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <ConfirmDialog
        open={confirmRemove !== null}
        title="Remove Member"
        message={
          confirmRemove
            ? `Are you sure you want to remove ${confirmRemove.fullName}? They will lose access immediately, and any tasks they were assigned to will become unassigned.`
            : ''
        }
        confirmLabel="Remove"
        confirming={removeMutation.isPending}
        onCancel={() => setConfirmRemove(null)}
        onConfirm={async () => {
          if (!confirmRemove) return
          await removeMutation.mutateAsync(confirmRemove.userId)
          setConfirmRemove(null)
        }}
      />
    </Modal>
  )
}

/**
 * Compact dropdown rendered next to each member row. Same 4 options + same
 * descriptions as the chip-input picker. Uses a portal so the menu can escape
 * the scrollable People-with-access list (which has overflow-y-auto and would
 * otherwise clip an absolutely-positioned child).
 */
function RowAccessPicker({ currentLevel, disabled, onChange }) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const [menuPos, setMenuPos] = useState(null)

  // Measure trigger position whenever the menu opens — fixed coords so the
  // portal can render the menu anywhere on screen without parent overflow
  // clipping it. Re-runs on viewport resize and scroll while open.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    function place() {
      const rect = triggerRef.current.getBoundingClientRect()
      setMenuPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onClick(e) {
      const t = e.target
      if (!menuRef.current?.contains(t) && !triggerRef.current?.contains(t)) {
        setOpen(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const menu =
    open && menuPos
      ? createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label="Access level"
            style={{
              position: 'fixed',
              top: menuPos.top,
              right: menuPos.right,
              zIndex: 80,
            }}
            className="w-64 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl overflow-hidden"
          >
            {ACCESS_OPTIONS.map((opt) => {
              const selected = opt.value === currentLevel
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    setOpen(false)
                    if (!selected) onChange(opt.value)
                  }}
                  className={
                    'w-full text-left px-4 py-2.5 cursor-pointer hover:bg-[color:var(--color-card-hover)] ' +
                    (selected ? 'bg-[color:var(--color-card-hover)]' : '')
                  }
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                        {opt.label}
                      </p>
                      <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
                        {opt.description}
                      </p>
                    </div>
                    {selected ? (
                      <Check
                        size={14}
                        className="mt-0.5 text-[color:var(--color-accent-dim)] shrink-0"
                      />
                    ) : null}
                  </div>
                </button>
              )
            })}
          </div>,
          document.body,
        )
      : null

  return (
    <div className="shrink-0">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      >
        {labelForAccessLevel(currentLevel)}
        <ChevronDown
          size={12}
          className={
            'text-[color:var(--color-ink-muted)] transition-transform ' +
            (open ? 'rotate-180' : '')
          }
        />
      </button>
      {menu}
    </div>
  )
}
